/**
 * Document Library — replaces the legacy thin "Procedures" page.
 *
 * Holds the static / globally-reusable docs (Procedures, SOPs, Generic
 * Work Instructions) that feed into the AI RAMS generator. Admin can
 * filter by kind, search by title/tag, create / edit / archive, and tag
 * each doc so the RAMS ranker picks it up.
 *
 * Backend: `/api/library` (GET/POST/PATCH/DELETE) — see `rams_routes.py`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Pencil, Trash2, Search, FileText, BookOpen, ListChecks,
  Eye, Download, PackageCheck, X, Mail,
  ShieldCheck, ShieldAlert, ClipboardCheck, Users2,
  Upload, Paperclip, GitBranch, History as HistoryIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { lineDiff } from '../../lib/lineDiff';
import api, {
  fetchLibraryPdfUrl, fetchLibraryBulkPackUrl, triggerDownload,
  emailLibraryBulkPack, getUsers, uploadLibraryFile, deleteLibraryFile,
} from '../../services/api';

// Every supported kind in the Compliance library. Keep this table in sync
// with `VALID_KINDS` in backend/rams_routes.py — the backend rejects any
// kind not in that set, and the per-doc PDF renderer in pdf_routes.py
// uses matching labels for the branded cover title.
const KIND_META = {
  procedure: { label: 'Procedure', icon: BookOpen, tone: 'bg-blue-50 text-blue-800 border-blue-200' },
  sop: { label: 'SOP', icon: ListChecks, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  work_instruction: { label: 'Work Instruction', icon: FileText, tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  policy: { label: 'Policy', icon: ShieldCheck, tone: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  insurance: { label: 'Insurance', icon: ShieldAlert, tone: 'bg-rose-50 text-rose-800 border-rose-200' },
  form: { label: 'Form', icon: ClipboardCheck, tone: 'bg-teal-50 text-teal-800 border-teal-200' },
  meeting_minutes: { label: 'Meeting Minutes', icon: Users2, tone: 'bg-purple-50 text-purple-800 border-purple-200' },
};

// Plural labels for the counter pills. Kept separate from the meta table
// so the singular label can stay concise in chips / selects.
const KIND_PLURAL = {
  procedure: 'Procedures',
  sop: 'SOPs',
  work_instruction: 'Work Instructions',
  policy: 'Policies',
  insurance: 'Insurance',
  form: 'Forms',
  meeting_minutes: 'Meeting Minutes',
};

const blankDoc = () => ({ kind: 'procedure', title: '', summary: '', body: '', tags: [], meta: {}, reference: '', revision: 1 });

// Kinds where the admin uploads a finished PDF rather than authoring the
// document in-app. For these we hide the body/summary/tags/meta inputs in
// the editor — the ask is "just have a PDF file upload" — and also skip
// auto-reference generation (insurance certs have their own policy numbers,
// forms/meetings are dated, etc.).
const UPLOAD_ONLY_KINDS = new Set(['insurance', 'form', 'meeting_minutes', 'policy']);

const DocumentLibrary = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState('all');
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [saving, setSaving] = useState(false);
  // Bulk pack selection — ordered array so the TOC matches the click order.
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk-pack mode: 'download' = open PDF in a new tab (default),
  // 'email' = send via Resend to a selected customer.
  const [bulkMode, setBulkMode] = useState('download');
  const [customers, setCustomers] = useState([]);
  const [bulkCustomerId, setBulkCustomerId] = useState('');
  const [bulkTo, setBulkTo] = useState([]);   // list of selected email addresses
  const [bulkCc, setBulkCc] = useState([]);
  const [bulkManualEmail, setBulkManualEmail] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  // In-page PDF preview (shared single/bulk). `previewUrl` null means closed.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  // Revision history dialog — `historyFor` holds the reference being viewed;
  // null means the dialog is closed. `diffFor` holds the older rev we're
  // comparing against the current rev in the side-by-side diff view.
  const [historyFor, setHistoryFor] = useState(null);
  const [historyRevs, setHistoryRevs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [diffFor, setDiffFor] = useState(null);
  // Staged-upload support for upload-only kinds: when the admin picks a PDF
  // on the *new* document form (no `editing.id` yet), we hold the File in
  // memory and POST it to the newly-minted doc during `save()`. This turns
  // the previously two-step "create stub → re-open → upload" into a single
  // flow, which is how a policy / insurance cert / form naturally arrives.
  const [pendingFile, setPendingFile] = useState(null);

  const refresh = () => api.get('/library').then((r) => setDocs(r.data || [])).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);
  // Load customers once for the "Email to customer" mode picker.
  useEffect(() => {
    getUsers()
      .then((u) => setCustomers((u || []).filter((x) => x.role === 'customer')))
      .catch(() => {});
  }, []);

  // Resolve the selected customer's known contact emails (primary + contacts).
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === bulkCustomerId) || null,
    [customers, bulkCustomerId],
  );
  const customerEmails = useMemo(() => {
    if (!selectedCustomer) return [];
    const out = [];
    const seen = new Set();
    const push = (e, label) => {
      const v = (e || '').trim();
      if (!v || seen.has(v.toLowerCase())) return;
      seen.add(v.toLowerCase());
      out.push({ email: v, label: label || v });
    };
    push(selectedCustomer.email, `${selectedCustomer.name || 'primary'} (primary)`);
    (selectedCustomer.contacts || []).forEach((c) => push(c.email, `${c.name || c.email} (contact)`));
    return out;
  }, [selectedCustomer]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filterKind !== 'all' && d.kind !== filterKind) return false;
      if (!q) return true;
      const hay = `${d.title} ${d.summary} ${(d.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [docs, filterKind, search]);

  // Totals per category (ignores search + kind filter so the counters show
  // the real library size at a glance).
  const counts = useMemo(() => {
    const c = Object.fromEntries(Object.keys(KIND_META).map((k) => [k, 0]));
    docs.forEach((d) => { if (c[d.kind] !== undefined) c[d.kind] += 1; });
    return c;
  }, [docs]);

  const openNew = () => {
    setEditing(blankDoc());
    setTagDraft('');
    setPendingFile(null);
    setEditorOpen(true);
  };
  const openEdit = (d) => {
    setEditing({ ...d, tags: [...(d.tags || [])] });
    setTagDraft('');
    setPendingFile(null);
    setEditorOpen(true);
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try {
      await api.delete(`/library/${d.id}`);
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };
  const openPdfPreview = async (d) => {
    setPreviewBusy(true);
    try {
      const { url, filename } = await fetchLibraryPdfUrl(d.id);
      setPreviewUrl(url);
      setPreviewFilename(filename);
    } catch (e) {
      toast.error(e?.message || 'Could not load PDF');
    } finally {
      setPreviewBusy(false);
    }
  };
  const downloadPdf = async (d) => {
    try {
      const { url, filename } = await fetchLibraryPdfUrl(d.id);
      triggerDownload(url, filename);
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e) {
      toast.error(e?.message || 'Could not download PDF');
    }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewFilename('');
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const clearSelection = () => setSelected([]);
  const openBulkDialog = () => {
    setBulkMode('download');
    setBulkCustomerId('');
    setBulkTo([]);
    setBulkCc([]);
    setBulkManualEmail('');
    setBulkNote('');
    setBulkOpen(true);
  };
  const toggleEmailInList = (list, setList, email) => {
    setList(list.includes(email) ? list.filter((x) => x !== email) : [...list, email]);
  };
  const addManualEmail = (bucket) => {
    const v = bulkManualEmail.trim();
    if (!v || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (bucket === 'to' && !bulkTo.includes(v)) setBulkTo([...bulkTo, v]);
    if (bucket === 'cc' && !bulkCc.includes(v)) setBulkCc([...bulkCc, v]);
    setBulkManualEmail('');
  };
  const buildBulkPack = async () => {
    if (!selected.length) return;
    if (bulkMode === 'email' && bulkTo.length === 0) {
      toast.error('Pick at least one "To" recipient');
      return;
    }
    setBulkBusy(true);
    try {
      if (bulkMode === 'email') {
        const res = await emailLibraryBulkPack({
          ids: selected,
          title: bulkTitle.trim() || null,
          to_list: bulkTo,
          cc: bulkCc,
          cc_admin: true,
          note: bulkNote.trim(),
          customerId: bulkCustomerId || null,
        });
        toast.success(`Pack emailed to ${(res?.to || []).join(', ')}`);
      } else {
        const { url, filename } = await fetchLibraryBulkPackUrl(selected, bulkTitle.trim() || null);
        setPreviewUrl(url);
        setPreviewFilename(filename);
        toast.success(`Pack built (${selected.length} documents)`);
      }
      setBulkOpen(false);
      setBulkTitle('');
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || 'Could not build bulk pack');
    } finally {
      setBulkBusy(false);
    }
  };
  const save = async () => {
    if (!editing.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      let savedId = editing.id;
      if (editing.id) {
        await api.patch(`/library/${editing.id}`, editing);
        toast.success('Updated');
      } else {
        const { data } = await api.post('/library', editing);
        savedId = data.id;
        toast.success('Created');
      }
      // If the admin staged a PDF during creation (upload-only kinds), POST
      // it now that we have a real id. Surface upload failures as a second
      // toast but don't rollback the create — the stub doc is still useful
      // (admin can retry the upload after re-opening it).
      if (pendingFile && savedId) {
        try {
          await uploadLibraryFile(savedId, pendingFile);
          toast.success(`PDF attached`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'PDF upload failed — you can retry from the edit dialog');
        }
      }
      setPendingFile(null);
      setEditorOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /** Fork a new revision of an authored doc (procedure / SOP / work
   * instruction). Backend clones the body/summary/tags/meta, bumps
   * `revision` to the next number, archives the previous rev. We then
   * open the new rev in the editor so the admin can edit what changed.
   */
  const revise = async (d) => {
    if (!window.confirm(`Create a new revision of "${d.title}"? The current version will move to the archive.`)) return;
    try {
      const { data: next } = await api.post(`/library/${d.id}/revise`);
      toast.success(`Revision ${next.revision} created`);
      await refresh();
      openEdit(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create revision');
    }
  };

  /** Fetch every rev under this doc's reference and open the history
   * panel. The backend returns newest-rev-first so we can render straight
   * from the array without re-sorting. */
  const openHistory = async (d) => {
    if (!d.reference) return;
    setHistoryFor(d);
    setHistoryLoading(true);
    setHistoryRevs([]);
    try {
      const { data } = await api.get(`/library/reference/${encodeURIComponent(d.reference)}/history`);
      setHistoryRevs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (!editing.tags.includes(t)) {
      setEditing({ ...editing, tags: [...editing.tags, t] });
    }
    setTagDraft('');
  };
  const removeTag = (t) => setEditing({ ...editing, tags: editing.tags.filter((x) => x !== t) });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance"
        description="Controlled library of procedures, SOPs, work instructions, policies, insurance documents, forms and meeting minutes. Re-used globally and fed into the AI RAMS generator for every new work order."
      />

      {/* Category counters — one pill per kind, clickable to filter */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="library-counters">
        {Object.entries(KIND_META).map(([k, m]) => {
          const Icon = m.icon;
          const active = filterKind === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilterKind(active ? 'all' : k)}
              className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                active
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : `${m.tone} hover:border-slate-300`
              }`}
              data-testid={`library-counter-${k}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon className="h-4 w-4 shrink-0" />
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider truncate">
                  {KIND_PLURAL[k]}
                </span>
              </div>
              <span
                className={`text-lg sm:text-xl font-bold tabular-nums ${active ? 'text-white' : 'text-slate-900'}`}
                data-testid={`library-counter-value-${k}`}
              >
                {counts[k] || 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, tag, summary…"
            className="pl-9"
            data-testid="library-search"
          />
        </div>
        <Select value={filterKind} onValueChange={setFilterKind}>
          <SelectTrigger className="w-full sm:w-56" data-testid="library-kind-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.entries(KIND_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={openNew} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="library-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New document
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500" data-testid="library-empty">
          No documents yet.
          {filterKind === 'all' && !search && (
            <div className="mt-2">Click <strong>New document</strong> to add the first procedure, SOP, policy or form.</div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((d) => {
            const meta = KIND_META[d.kind] || KIND_META.procedure;
            const Icon = meta.icon;
            const isSelected = selected.includes(d.id);
            return (
              <div
                key={d.id}
                className={`bg-white border rounded-md p-4 flex flex-col gap-2 transition-colors ${
                  isSelected ? 'border-slate-900 ring-2 ring-slate-900/20' : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`library-card-${d.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(d.id)}
                      className="mt-0.5"
                      data-testid={`library-select-${d.id}`}
                    />
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${meta.tone}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-600 hover:text-slate-900" onClick={() => openPdfPreview(d)} disabled={previewBusy} data-testid={`library-preview-${d.id}`} title="Preview PDF">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-600 hover:text-slate-900" onClick={() => downloadPdf(d)} data-testid={`library-download-${d.id}`} title="Download PDF">
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(d)} data-testid={`library-edit-${d.id}`} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {/* Revise: only for authored kinds that have a reference.
                        Upload-only docs (insurance certs, policies, forms,
                        meeting minutes) are unique physical documents — a
                        new policy is a new policy, not a rev. */}
                    {['procedure', 'sop', 'work_instruction'].includes(d.kind) && d.reference && (
                      <>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-slate-600 hover:text-indigo-700"
                          onClick={() => revise(d)}
                          data-testid={`library-revise-${d.id}`}
                          title="Create a new revision"
                        >
                          <GitBranch className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-slate-600 hover:text-slate-900"
                          onClick={() => openHistory(d)}
                          data-testid={`library-history-${d.id}`}
                          title="Revision history"
                        >
                          <HistoryIcon className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-800" onClick={() => remove(d)} data-testid={`library-delete-${d.id}`} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="font-semibold text-slate-900 leading-tight">{d.title}</div>
                {(d.reference || d.revision) && (
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid={`library-ref-${d.id}`}>
                    {d.reference && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-900 text-white text-[10px] font-mono tracking-wide">
                        {d.reference}
                      </span>
                    )}
                    {d.revision && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono">
                        Rev.{d.revision}
                      </span>
                    )}
                  </div>
                )}
                {d.summary && <div className="text-xs text-slate-500 line-clamp-3">{d.summary}</div>}
                {d.tags && d.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-auto pt-2">
                    {d.tags.map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px] font-normal">{t}</Badge>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Floating bulk-pack action bar (appears when ≥1 doc selected) */}
      {selected.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl pl-5 pr-2 py-2 flex items-center gap-3"
          data-testid="library-bulk-bar"
        >
          <span className="text-sm font-semibold tabular-nums">
            {selected.length} selected
          </span>
          <Button
            size="sm"
            onClick={openBulkDialog}
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-full h-8 px-3 text-xs font-semibold"
            data-testid="library-bulk-build-btn"
          >
            <PackageCheck className="h-3.5 w-3.5 mr-1.5" /> Build pack
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={clearSelection}
            className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8 rounded-full"
            data-testid="library-bulk-clear-btn"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Bulk-pack title dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto" data-testid="library-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Build document pack</DialogTitle>
            <DialogDescription>
              Combines the {selected.length} selected {selected.length === 1 ? 'document' : 'documents'} into a single
              Lutrom-branded PDF booklet with a cover page and table of contents.
            </DialogDescription>
          </DialogHeader>

          {/* Mode switch */}
          <div className="grid grid-cols-2 gap-2" data-testid="library-bulk-mode-switch">
            <button
              type="button"
              onClick={() => setBulkMode('download')}
              className={`rounded-md border px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                bulkMode === 'download'
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
              data-testid="library-bulk-mode-download"
            >
              <Eye className="h-4 w-4" /> Preview
            </button>
            <button
              type="button"
              onClick={() => setBulkMode('email')}
              className={`rounded-md border px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                bulkMode === 'email'
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
              data-testid="library-bulk-mode-email"
            >
              <Mail className="h-4 w-4" /> Email
            </button>
          </div>

          <div>
            <Label>Pack title (optional)</Label>
            <Input
              value={bulkTitle}
              onChange={(e) => setBulkTitle(e.target.value)}
              placeholder="e.g. ACME Foods · Induction Pack"
              className="mt-1.5"
              data-testid="library-bulk-title-input"
            />
          </div>

          {bulkMode === 'email' && (
            <div className="space-y-3 pt-1" data-testid="library-bulk-email-fields">
              <div>
                <Label>Customer</Label>
                <Select value={bulkCustomerId} onValueChange={(v) => {
                  setBulkCustomerId(v);
                  // Pre-select the primary email on customer change
                  const c = customers.find((x) => x.id === v);
                  if (c?.email) setBulkTo([c.email]); else setBulkTo([]);
                }}>
                  <SelectTrigger className="mt-1.5" data-testid="library-bulk-customer-select">
                    <SelectValue placeholder="Pick a customer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company || c.name} {c.email ? <span className="text-slate-400">· {c.email}</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {customerEmails.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Saved contacts</Label>
                  <div className="mt-1.5 space-y-1">
                    {customerEmails.map((c) => (
                      <div key={c.email} className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate text-slate-700">{c.label}</span>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => toggleEmailInList(bulkTo, setBulkTo, c.email)}
                            className={`px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider ${
                              bulkTo.includes(c.email) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'
                            }`}
                            data-testid={`library-bulk-to-toggle-${c.email}`}
                          >
                            To
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleEmailInList(bulkCc, setBulkCc, c.email)}
                            className={`px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider ${
                              bulkCc.includes(c.email) ? 'bg-slate-700 text-white border-slate-700' : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'
                            }`}
                            data-testid={`library-bulk-cc-toggle-${c.email}`}
                          >
                            Cc
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Add manual email</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={bulkManualEmail}
                    onChange={(e) => setBulkManualEmail(e.target.value)}
                    placeholder="somebody@company.com"
                    data-testid="library-bulk-manual-email"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => addManualEmail('to')} data-testid="library-bulk-manual-add-to">+ To</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => addManualEmail('cc')} data-testid="library-bulk-manual-add-cc">+ Cc</Button>
                </div>
              </div>

              {(bulkTo.length > 0 || bulkCc.length > 0) && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 space-y-1.5 text-xs">
                  {bulkTo.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</span>
                      {bulkTo.map((e) => (
                        <button key={e} type="button" onClick={() => setBulkTo(bulkTo.filter((x) => x !== e))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-900 bg-slate-900 text-white">
                          {e} <span className="text-slate-300">×</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {bulkCc.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cc</span>
                      {bulkCc.map((e) => (
                        <button key={e} type="button" onClick={() => setBulkCc(bulkCc.filter((x) => x !== e))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-700 bg-slate-700 text-white">
                          {e} <span className="text-slate-300">×</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Message (optional)</Label>
                <textarea
                  rows={3}
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  placeholder="Short note to include above the document list."
                  data-testid="library-bulk-note"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              onClick={buildBulkPack}
              disabled={bulkBusy}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="library-bulk-build-confirm"
            >
              {bulkMode === 'email' ? <Mail className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
              {bulkBusy
                ? (bulkMode === 'email' ? 'Sending…' : 'Building…')
                : (bulkMode === 'email' ? 'Send to customer' : 'Build & preview')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        {editing && (
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="library-editor">
            <DialogHeader>
              <DialogTitle>{editing.id ? 'Edit document' : 'New document'}</DialogTitle>
              <DialogDescription>
                Tag the document with the equipment / hazard keywords the RAMS generator should match against (e.g. "electrical", "lockout", "compressor").
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="library-editor-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder="e.g. Lock-out Tag-out for 415 V three-phase supplies"
                  className="mt-1.5"
                  data-testid="library-editor-title"
                />
              </div>
              {/* Reference + revision — auto-filled on create for
                  procedure / sop / work_instruction (PCD_WMS style).
                  Admins can still override before saving; revision is
                  manually incremented on every new rev of the doc. */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <div>
                  <Label>Reference {!editing.id && !UPLOAD_ONLY_KINDS.has(editing.kind) && <span className="text-xs text-slate-400 font-normal">(auto if left blank)</span>}</Label>
                  <Input
                    value={editing.reference || ''}
                    onChange={(e) => setEditing({ ...editing, reference: e.target.value })}
                    placeholder={
                      UPLOAD_ONLY_KINDS.has(editing.kind)
                        ? 'e.g. EL-2026-001 (optional)'
                        : 'Auto: PCD_WMS'
                    }
                    className="mt-1.5 font-mono"
                    data-testid="library-editor-reference"
                  />
                </div>
                <div className="w-full sm:w-28">
                  <Label>Revision</Label>
                  <Input
                    type="number"
                    min={1}
                    value={editing.revision ?? 1}
                    onChange={(e) => setEditing({ ...editing, revision: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    className="mt-1.5"
                    data-testid="library-editor-revision"
                  />
                </div>
              </div>
              {/* Summary line — only shown for authored docs (procedures,
                  SOPs, work instructions). For upload-only kinds the PDF
                  is the document, so we skip the in-app summary/body. */}
              {!UPLOAD_ONLY_KINDS.has(editing.kind) && (
                <div>
                  <Label>Summary</Label>
                  <Input
                    value={editing.summary}
                    onChange={(e) => setEditing({ ...editing, summary: e.target.value })}
                    placeholder="One-line description shown on the card"
                    className="mt-1.5"
                    data-testid="library-editor-summary"
                  />
                </div>
              )}

              {/* Admin-uploaded PDF — insurance certs, signed policies, blank
                  forms, meeting minutes. Visible from the first render so the
                  admin can pick the file as part of the create flow (we
                  stash it in `pendingFile` and upload it in save() after the
                  stub doc is created). For already-saved docs this is the
                  live attachment manager — replace / remove / download. */}
              {UPLOAD_ONLY_KINDS.has(editing.kind) && (
                <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50" data-testid="library-editor-file-upload">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1">
                      <Paperclip className="h-3.5 w-3.5" /> Attached PDF
                    </div>
                    {editing.fileStoredAs && (
                      <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                        Served on download
                      </span>
                    )}
                    {!editing.id && pendingFile && (
                      <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                        Uploads on save
                      </span>
                    )}
                  </div>
                  {/* Three states:
                      (1) Saved doc with an attached PDF → show attached card
                      (2) Unsaved doc with a staged File  → show staged card
                      (3) Neither                         → show empty state */}
                  {editing.fileStoredAs ? (
                    <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate" data-testid="library-editor-file-name">
                          {editing.fileName || editing.fileStoredAs}
                        </div>
                        <div className="text-xs text-slate-500">
                          {editing.filePages ? `${editing.filePages} page${editing.filePages !== 1 ? 's' : ''}` : ''}
                          {editing.fileSize ? ` · ${Math.round(editing.fileSize / 1024)} KB` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm" variant="outline"
                          onClick={async () => {
                            try {
                              const { url, filename } = await fetchLibraryPdfUrl(editing.id);
                              triggerDownload(url, filename);
                            } catch (e) {
                              toast.error(e?.message || 'Preview failed');
                            }
                          }}
                          data-testid="library-editor-file-download"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="text-red-600"
                          onClick={async () => {
                            if (!window.confirm('Remove the attached PDF? The document will fall back to the generated layout.')) return;
                            try {
                              const updated = await deleteLibraryFile(editing.id);
                              setEditing({ ...editing, ...updated, fileStoredAs: undefined, fileUrl: undefined, fileName: undefined, fileSize: undefined, filePages: undefined, fileUploadedAt: undefined });
                              toast.success('File removed');
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Remove failed');
                            }
                          }}
                          data-testid="library-editor-file-remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ) : pendingFile ? (
                    <div className="flex items-center justify-between gap-2 bg-white border border-blue-200 rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate" data-testid="library-editor-pending-file-name">
                          {pendingFile.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {Math.round(pendingFile.size / 1024)} KB · will upload when you save
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost" className="text-red-600 shrink-0"
                        onClick={() => setPendingFile(null)}
                        data-testid="library-editor-pending-file-remove"
                        title="Remove staged file"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500">
                      {editing.id
                        ? 'No PDF attached. Upload a file below and it will be served whenever someone downloads this document — bypassing the generated layout.'
                        : 'Pick a PDF now — it will upload automatically when you save the document.'}
                    </div>
                  )}
                  <label className="inline-flex">
                    <input
                      type="file" accept="application/pdf" hidden
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        // Not-yet-saved doc: stash the File and upload during
                        // save(). Already-saved doc: upload immediately as
                        // before so the admin sees the result in-dialog.
                        if (!editing.id) {
                          setPendingFile(f);
                          return;
                        }
                        try {
                          const updated = await uploadLibraryFile(editing.id, f);
                          setEditing({ ...editing, ...updated });
                          toast.success(`Uploaded · ${updated.filePages || 1} page${(updated.filePages || 1) !== 1 ? 's' : ''}`);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Upload failed');
                        }
                      }}
                      data-testid="library-editor-file-input"
                    />
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={(e) => e.currentTarget.previousSibling?.click?.()}
                      data-testid="library-editor-file-upload-btn"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      {editing.fileStoredAs
                        ? 'Replace PDF'
                        : pendingFile
                          ? 'Pick a different PDF'
                          : 'Upload PDF'}
                    </Button>
                  </label>
                </div>
              )}

              {/* Kind-specific structured metadata — shown only for authored
                  docs. Upload-only kinds (insurance/form/policy/meeting_minutes)
                  use the uploaded PDF as the source of truth. */}
              {!UPLOAD_ONLY_KINDS.has(editing.kind) && editing.kind === 'policy' && (
                <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50" data-testid="library-editor-policy-meta">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Review &amp; sign-off</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Review cadence (months)</Label>
                      <Input
                        type="number" min="1"
                        value={editing.meta?.reviewCadenceMonths || ''}
                        onChange={(e) => setEditing({ ...editing, meta: { ...(editing.meta || {}), reviewCadenceMonths: e.target.value ? Number(e.target.value) : '' } })}
                        placeholder="12"
                        data-testid="library-editor-review-cadence"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Approver</Label>
                      <Input
                        value={editing.meta?.approver || ''}
                        onChange={(e) => setEditing({ ...editing, meta: { ...(editing.meta || {}), approver: e.target.value } })}
                        placeholder="e.g. Managing Director"
                        data-testid="library-editor-approver"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Approved on</Label>
                      <Input
                        type="date"
                        value={editing.meta?.approvedAt || ''}
                        onChange={(e) => setEditing({ ...editing, meta: { ...(editing.meta || {}), approvedAt: e.target.value } })}
                        data-testid="library-editor-approved-at"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Next review</Label>
                      <Input
                        type="date"
                        value={editing.meta?.nextReview || ''}
                        onChange={(e) => setEditing({ ...editing, meta: { ...(editing.meta || {}), nextReview: e.target.value } })}
                        data-testid="library-editor-next-review"
                      />
                    </div>
                  </div>
                </div>
              )}

              {!UPLOAD_ONLY_KINDS.has(editing.kind) && editing.kind === 'meeting_minutes' && (
                <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50" data-testid="library-editor-meeting-meta">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Meeting details</div>
                  <div>
                    <Label className="text-xs">Attendees (one per line)</Label>
                    <textarea
                      rows={3}
                      value={(editing.meta?.attendees || []).join('\n')}
                      onChange={(e) => setEditing({ ...editing, meta: { ...(editing.meta || {}), attendees: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) } })}
                      placeholder={'Alex Smith\nMaria Jones\n…'}
                      className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-200"
                      data-testid="library-editor-attendees"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Action items</Label>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setEditing({ ...editing, meta: { ...(editing.meta || {}), actionItems: [...((editing.meta || {}).actionItems || []), { owner: '', action: '', dueDate: '' }] } })}
                        data-testid="library-editor-add-action"
                      >
                        + Add action
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {((editing.meta || {}).actionItems || []).map((a, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                          <Input
                            className="col-span-3 bg-white"
                            value={a.owner || ''}
                            onChange={(e) => {
                              const items = [...((editing.meta || {}).actionItems || [])];
                              items[i] = { ...items[i], owner: e.target.value };
                              setEditing({ ...editing, meta: { ...(editing.meta || {}), actionItems: items } });
                            }}
                            placeholder="Owner"
                            data-testid={`library-editor-action-owner-${i}`}
                          />
                          <Input
                            className="col-span-6 bg-white"
                            value={a.action || ''}
                            onChange={(e) => {
                              const items = [...((editing.meta || {}).actionItems || [])];
                              items[i] = { ...items[i], action: e.target.value };
                              setEditing({ ...editing, meta: { ...(editing.meta || {}), actionItems: items } });
                            }}
                            placeholder="Action"
                            data-testid={`library-editor-action-text-${i}`}
                          />
                          <Input
                            className="col-span-2 bg-white"
                            type="date"
                            value={a.dueDate || ''}
                            onChange={(e) => {
                              const items = [...((editing.meta || {}).actionItems || [])];
                              items[i] = { ...items[i], dueDate: e.target.value };
                              setEditing({ ...editing, meta: { ...(editing.meta || {}), actionItems: items } });
                            }}
                            data-testid={`library-editor-action-due-${i}`}
                          />
                          <Button
                            type="button" variant="ghost" size="icon"
                            className="col-span-1 h-8 w-8 text-red-600"
                            onClick={() => {
                              const items = ((editing.meta || {}).actionItems || []).filter((_, j) => j !== i);
                              setEditing({ ...editing, meta: { ...(editing.meta || {}), actionItems: items } });
                            }}
                            data-testid={`library-editor-action-remove-${i}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {!UPLOAD_ONLY_KINDS.has(editing.kind) && (
                <>
                  <div>
                    <Label>Body / steps</Label>
                    <textarea
                      rows={8}
                      value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                      className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                      placeholder={'1. Inform the area supervisor.\n2. Move the local isolator to OFF.\n3. Apply your personal padlock + tag.\n…'}
                      data-testid="library-editor-body"
                    />
                  </div>
                  <div>
                    <Label>Tags</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        value={tagDraft}
                        onChange={(e) => setTagDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        placeholder="electrical, lockout, compressor…"
                        data-testid="library-editor-tag-input"
                      />
                      <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                    </div>
                    {editing.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {editing.tags.map((t) => (
                          <button
                            key={t} type="button" onClick={() => removeTag(t)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 text-xs text-slate-700 hover:bg-slate-50"
                          >
                            {t} <span className="text-slate-400">×</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="library-editor-save"
              >
                {saving ? 'Saving…' : editing.id ? 'Save changes' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Revision history panel — lists every rev (archived + current)
          for the selected reference, with a diff button against the
          current rev. */}
      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) { setHistoryFor(null); setHistoryRevs([]); } }}>
        {historyFor && (
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="library-history-dialog">
            <DialogHeader>
              <DialogTitle>Revision history · {historyFor.reference}</DialogTitle>
              <DialogDescription>
                All revs ever issued under this reference, newest first. Tap
                Compare to diff an older rev against the current one.
              </DialogDescription>
            </DialogHeader>
            {historyLoading ? (
              <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
            ) : historyRevs.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">No revisions found.</div>
            ) : (
              <ul className="divide-y divide-slate-200 border border-slate-200 rounded-md">
                {historyRevs.map((r) => {
                  const isCurrent = !r.archived;
                  return (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5"
                      data-testid={`library-history-row-${r.id}`}
                    >
                      <div className="min-w-0 flex items-center gap-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono ${isCurrent ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                          Rev.{r.revision}
                        </span>
                        <span className="text-xs text-slate-500">
                          {(r.createdAt || '').slice(0, 10)}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide">Current</span>
                        )}
                      </div>
                      {!isCurrent && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => openPdfPreview(r)}
                            disabled={previewBusy}
                            data-testid={`library-history-pdf-${r.id}`}
                            title="Open this revision as a PDF exactly as it was issued"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setDiffFor(r)}
                            data-testid={`library-history-diff-${r.id}`}
                          >
                            Compare vs current
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </DialogContent>
        )}
      </Dialog>

      {/* Side-by-side body diff. `diffFor` is the older rev, compared
          against the current (non-archived) rev for the same reference. */}
      <Dialog open={!!diffFor} onOpenChange={(o) => { if (!o) setDiffFor(null); }}>
        {diffFor && (() => {
          const current = historyRevs.find((r) => !r.archived);
          const rows = lineDiff(diffFor.body || '', current?.body || '');
          const added = rows.filter(r => r.type === 'add').length;
          const removed = rows.filter(r => r.type === 'del').length;
          return (
            <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="library-diff-dialog">
              <DialogHeader>
                <DialogTitle>
                  Rev.{diffFor.revision} → Rev.{current?.revision ?? '—'}
                </DialogTitle>
                <DialogDescription>
                  <span className="text-emerald-700 font-medium">+{added}</span>
                  {' added · '}
                  <span className="text-rose-700 font-medium">−{removed}</span>
                  {' removed'}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-0 border border-slate-200 rounded-md overflow-hidden text-[12px] font-mono" data-testid="library-diff-rows">
                <div className="bg-slate-50 px-2 py-1 border-b border-slate-200 text-[11px] text-slate-600 uppercase tracking-wider">
                  Rev.{diffFor.revision} (older)
                </div>
                <div className="bg-slate-50 px-2 py-1 border-b border-slate-200 text-[11px] text-slate-600 uppercase tracking-wider">
                  Rev.{current?.revision ?? '—'} (current)
                </div>
                {rows.map((r, i) => (
                  <React.Fragment key={i}>
                    <div
                      className={`px-2 py-0.5 border-b border-slate-100 whitespace-pre-wrap break-words ${
                        r.type === 'del' ? 'bg-rose-50 text-rose-900'
                        : r.type === 'add' ? 'bg-slate-50 text-slate-300'
                        : ''
                      }`}
                    >
                      {r.left ?? ''}
                    </div>
                    <div
                      className={`px-2 py-0.5 border-b border-slate-100 whitespace-pre-wrap break-words ${
                        r.type === 'add' ? 'bg-emerald-50 text-emerald-900'
                        : r.type === 'del' ? 'bg-slate-50 text-slate-300'
                        : ''
                      }`}
                    >
                      {r.right ?? ''}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>

      {/* In-page PDF preview — shared by single-doc and bulk-pack flows */}
      <PdfPreviewDialog
        url={previewUrl}
        filename={previewFilename}
        onClose={closePreview}
        title="Document preview"
      />
    </div>
  );
};

export default DocumentLibrary;
